import React, { useEffect, useRef, useState } from 'react';
import Footer from './Footer';
import Header from './Header';
import { EMarketplaceLayoutProvider } from './EMarketplaceLayoutContext';
import Breadcrumb from './Breadcrumb';
import Banner from './Banner';
import EmarketplaceAlertBar from './EmarketplaceAlertBar';
import PageRoute from '../../utils/constants/PageRoute';
import { usePTVNAuthentication } from '@ptvn-react/authentication';
import {
  CompanySubscriptionPackageSettingResponse,
  MyProfileResponse,
  SubscriptionData,
} from '../../api/response/AuthorizeEMarketplace';
import { useHistory, useLocation } from 'react-router';
import { JobBoardRoute, UserRoleType } from '../../utils/constants/JobBoardConstants';
import urljoin from 'url-join';
import { AuthorizeEmarketplaceClient } from '../../api/AuthorizeEMarketplaceClient';
import { CommonHelper } from '../../utils/helpers/CommonHelper';
import { setCompanySubscriptionPlanData } from '../../store/SubscriptionPlan/SubscriptionPlanSlice';
import { useDispatch } from 'react-redux';
import {
  SubscriptionPlanStatus,
  CompanySubscriptionPlan,
  SubscriptionPlanEnum,
  PaymentStatus,
} from '../../models/SubscriptionPlan';
import { useModalStack } from '../modal/ModalStackProvider';
import { ConfirmModal } from '../modal/ConfirmModal';
import { AlertModal } from '../modal/AlertModal';
import { ModalSize } from '../../utils/constants/StyleConstant';
import { useTranslation } from 'react-i18next';
import { get, isEmpty, isUndefined } from 'lodash';
import Cookies from 'js-cookie';
import { CookieName } from '../../utils/constants/CommonConstants';
import { PIModuleProvider } from '../../pages/PI/PIModuleContext';
import { OptionModal } from '../modal/OptionModal';
import FreeTrialImg from '../../../assets/images/intelligence/illustration/upgrade-icon-3.png';
import PackagePlanExpire from '../../../assets/images/intelligence/illustration/upgrade-icon-1.png';
import { SubscriptionPlanPageRoute } from '../../pages/SubscriptionPlan/SubscriptionPlanPageRoute';
import TermModal from '../modal/TermModal';
import { CUST_CARE_PHONE } from '../../utils/constants/MarketplaceConstant';
import APIConstant from '../../utils/constants/APIConstant';
import { FREE_TRIAL_PLAN_ID } from '../../utils/helpers/SubscriptionPlanHelper';

// Get the previous state
const usePrevious = (value: any) => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

export default function EMarketplaceLayout(
  props: React.PropsWithChildren<{
    userRole: UserRoleType | undefined;
  }>,
): React.FunctionComponentElement<any> {
  const fixedBreadcrumb = document.URL.includes(PageRoute.PACKAGE_SETTING);
  const fixedBreadcrumbNoContainerWrapper = document.URL.includes(PageRoute.MANAGE_CENTER);
  const fixedWrapperHasLeftSideBar = document.URL.includes(
    urljoin(PageRoute.SUBSCRIPTION_PLAN, SubscriptionPlanPageRoute.WELCOME),
  );
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const userProfile = usePTVNAuthentication<MyProfileResponse>().extendedUserInformation;
  const modalStack = useModalStack();
  const [resData, setResData] = useState<CompanySubscriptionPackageSettingResponse>();
  const [openPopup, setOpenPopup] = useState(false);
  const history = useHistory();

  const handleSubmitConsent = (subscriptionPlanID: number) => {
    history.push(
      urljoin(
        PageRoute.SUBSCRIPTION_PLAN,
        SubscriptionPlanPageRoute.PAYMENT + `?package=${subscriptionPlanID}`,
      ),
    );
  };

  const showTermModal = (subscriptionPlanID: number) => {
    modalStack.push(TermModal, {
      onClose: () => {},
      onSubmit: () => handleSubmitConsent(subscriptionPlanID),
    });
  };

  const onHandleShowingPopup = async (
    userRole: UserRoleType | undefined,
    userProfile: MyProfileResponse,
    openPopup: boolean,
  ) => {
    if (openPopup) {
      if (resData) {
        const subscriptionStatus = resData.subscriptionStatus || '';
        const subscriptionPlan = resData.title || '';

        // Check whether the package is out of date or not
        if (
          !isUndefined(resData.endDate) &&
          (resData.paymentStatus === PaymentStatus.VERIFIED ||
            resData.subscriptionPlanID === FREE_TRIAL_PLAN_ID)
        ) {
          if (userRole && userProfile && userProfile.companies) {
            const endDate = new Date(resData.endDate);
            endDate.setHours(0, 0, 0, 0);
            const nowDate = new Date();
            nowDate.setHours(0, 0, 0, 0);
            const diffTimeMs = Math.abs(endDate.getTime() - nowDate.getTime());
            const diffDays = Math.ceil(diffTimeMs / (24 * 60 * 60 * 1000));
            const supplierSubscriptionID = resData.supplierSubscriptionID || '';

            // For expired / almost expired modal
            if (diffDays < 16 && diffDays > 0) {
              if (diffDays < 8) {
                Cookies.remove(
                  CookieName.SUPPLIER_ALERT_ALMOST_EXPIRE_TIME_FIRST_PERIOD +
                    supplierSubscriptionID.toString(),
                );
                const cookieAlertExpire = Cookies.get(
                  CookieName.SUPPLIER_ALERT_ALMOST_EXPIRE_TIME_SECOND_PERIOD +
                    supplierSubscriptionID.toString(),
                );
                if (isUndefined(cookieAlertExpire)) {
                  Cookies.set(
                    CookieName.SUPPLIER_ALERT_ALMOST_EXPIRE_TIME_SECOND_PERIOD +
                      supplierSubscriptionID.toString(),
                    new Date().setHours(0, 0, 0, 0).toString(),
                  );

                  almostOver(
                    subscriptionPlan,
                    resData.title,
                    PaymentStatus.VERIFIED === resData.paymentStatus ||
                      resData.subscriptionPlanID === FREE_TRIAL_PLAN_ID,
                    diffDays,
                  );
                }
                return;
              }

              const cookieAlertExpire = Cookies.get(
                CookieName.SUPPLIER_ALERT_ALMOST_EXPIRE_TIME_FIRST_PERIOD +
                  supplierSubscriptionID.toString(),
              );
              if (isUndefined(cookieAlertExpire)) {
                Cookies.set(
                  CookieName.SUPPLIER_ALERT_ALMOST_EXPIRE_TIME_FIRST_PERIOD +
                    supplierSubscriptionID.toString(),
                  new Date().setHours(0, 0, 0, 0).toString(),
                );

                almostOver(
                  subscriptionPlan,
                  resData.title,
                  PaymentStatus.VERIFIED === resData.paymentStatus ||
                    resData.subscriptionPlanID === FREE_TRIAL_PLAN_ID,
                  diffDays,
                );
              }
            } else if (diffDays <= 0) {
              alertModalPackageExpired(userRole, resData.title);
            }
          }
        } else if (resData.subscriptionStatus === SubscriptionPlanStatus.INACTIVE) {
          alertModalSuspendSupplier(resData.title);
        }

        if (props.userRole === UserRoleType.SUPPLIER) {
          const cookieFirstUpgradePackage = Cookies.get(
            CookieName.SUPPLIER_ALERT_FIRST_UPGRADE_PACKAGE,
          );

          // No permission case
          if (
            ![
              SubscriptionPlanStatus.ACTIVE as string,
              SubscriptionPlanStatus.PENDING as string,
              SubscriptionPlanStatus.INACTIVE as string,
            ].includes(resData.subscriptionStatus)
          ) {
            if (
              resData.subscriptionStatus === SubscriptionPlanStatus.EXPIRED &&
              userProfile?.companies[0]?.roles?.filter((r) => r.isAdmin)?.length!
            ) {
              alertModalPackageExpired(userRole, resData.title);
            } else {
              history.push({
                pathname: PageRoute.NO_PERMISSION_SUBSCRIPTION,
                search: `?package=${resData.subscriptionPlanID}`,
                state: {
                  firstName: resData?.firstName,
                  lastName: resData?.lastName,
                  email: resData?.email,
                },
              });
            }
          }
          // Awaiting verification modal case
          if (
            resData.subscriptionPlanID !== FREE_TRIAL_PLAN_ID &&
            resData.paymentStatus !== PaymentStatus.VERIFIED
          ) {
            if (resData.isFirstTime) {
              modalStack.push(OptionModal, {
                data: {
                  modalSize: ModalSize.MD,
                  title: t('SubscriptionPlan.Modal.AwaitVerify'),
                  image: FreeTrialImg,
                  firstOptionText: t('SubscriptionPlan.Modal.BtnBackPrevPage'),
                  isActive: false,
                  isSingleButton: true,
                },
                hasCloseBtn: false,
                onClose: () => {},
                onFirstOptionClick: () =>
                  window.location.assign(APIConstant.supplierConnect.redirect),
                children: (
                  <div className="mt-16">
                    <p>{t('SubscriptionPlan.Modal.ThankForJoining')}</p>
                    <p>
                      {t('SubscriptionPlan.Modal.AnyQuestions')} <br />
                      {t('SubscriptionPlan.Modal.CustomerCare', {
                        phoneNumber: CUST_CARE_PHONE,
                      })}{' '}
                      <a href="mailto:customercare@pantavanij.com" className="btn-link">
                        customercare@pantavanij.com{' '}
                      </a>
                      <br />{' '}
                      {t('SubscriptionPlan.Modal.SupplierManagement', {
                        phoneNumber: '02-034-4488',
                      })}{' '}
                      <a href="mailto:SupplierWebWork@pantavanij.com" className="btn-link">
                        SupplierWebWork@pantavanij.com{' '}
                      </a>{' '}
                    </p>
                  </div>
                ),
              });
            }

            // If user upgrade package and still have old package to use while waiting for package verification
            if (!resData.isFirstTime && !cookieFirstUpgradePackage) {
              Cookies.set(
                CookieName.SUPPLIER_ALERT_FIRST_UPGRADE_PACKAGE,
                new Date().setHours(0, 0, 0, 0).toString(),
              );

              modalStack.push(OptionModal, {
                data: {
                  modalSize: ModalSize.MD,
                  title: t('SubscriptionPlan.Modal.AwaitVerifyUpgrade'),
                  image: FreeTrialImg,
                  firstOptionText: t('SubscriptionPlan.Modal.BtnBackHome'),
                  isActive: false,
                  isSingleButton: true,
                },
                hasCloseBtn: false,
                onClose: () => {},
                onFirstOptionClick: () => CommonHelper.navigateToUrl(PageRoute.PI),
                children: (
                  <div className="mt-16">
                    <p>{t('SubscriptionPlan.Modal.ThankForUpgrading')}</p>
                    <p>
                      {t('SubscriptionPlan.Modal.CustomerCare', {
                        phoneNumber: CUST_CARE_PHONE,
                      })}{' '}
                      <a href="mailto:customercare@pantavanij.com" className="btn-link">
                        customercare@pantavanij.com{' '}
                      </a>
                      <br />{' '}
                      {t('SubscriptionPlan.Modal.SupplierManagement', {
                        phoneNumber: '02-034-4488',
                      })}{' '}
                      <a href="mailto:SupplierWebWork@pantavanij.com" className="btn-link">
                        SupplierWebWork@pantavanij.com{' '}
                      </a>{' '}
                    </p>
                  </div>
                ),
              });
            }
          }
        }
        if (props.userRole === UserRoleType.BUYER) {
          if (resData.title === SubscriptionPlanEnum.FREE) {
            alertModalFreeUser(1);
          }
        }
      }
    }
    if (props.userRole === UserRoleType.BUYER) {
      const hasShownPopup = Cookies.get(CookieName.BUYER_FIRST_TIME_POPUP);
      if (!hasShownPopup) {
        Cookies.set(CookieName.BUYER_FIRST_TIME_POPUP, new Date().setHours(0, 0, 0, 0).toString());
        modalStack.push(TermModal, {
          onClose: () => {
            Cookies.remove(CookieName.BUYER_FIRST_TIME_POPUP);
            CommonHelper.navigateToUrl(PageRoute.LOGOUT);
          },
          onSubmit: () => {},
        });
      }
    }
  };

  const prevCompanies = usePrevious(userProfile?.companies) || [];
  const prevPathname = usePrevious(location.pathname) || '';

  const onGetSubsriptionPlan = async () => {
    try {
      const res: CompanySubscriptionPackageSettingResponse =
        await AuthorizeEmarketplaceClient.getCompanySubscriptionPlan(
          userProfile.companies[0].eMarketplaceCompanyID,
          props.userRole === UserRoleType.BUYER ? 1 : 2,
        ).toPromise();
      dispatch(setCompanySubscriptionPlanData(res as CompanySubscriptionPlan));

      setResData(res);
      setOpenPopup(true);
    } catch (err: any) {
      dispatch(setCompanySubscriptionPlanData({}));
      if (err && err.data && !isEmpty(err.data.errors)) {
        const errCode = err.data.errors[0].code;
        switch (errCode) {
          // not found supplier subscription
          case '4403':
            if (userProfile?.companies[0]?.roles[0]?.roleName === UserRoleType.SUPPLIER) {
              CommonHelper.navigateToUrl(PageRoute.SUBSCRIPTION_PLAN);
            }
            break;
          // user unable to access procurement board
          case '400':
            // alertModalUserNoPerMission();
            break;
          // user not found
          case '4401':
          default:
            CommonHelper.navigateToUrl(PageRoute.UNAUTHORIZED);
        }
      }
    }
  };

  useEffect(() => {
    if (location.pathname.includes('/pi')) {
      onGetSubsriptionPlan();
    }
  }, [props.userRole, location.pathname]);

  useEffect(() => {
    if (
      userProfile &&
      userProfile.companies &&
      userProfile.companies.length !== prevCompanies?.length
    ) {
      if (location.pathname.includes(PageRoute.PACKAGE_SETTING)) {
        if (!userProfile.companies[0].roles[0].isAdmin) {
          CommonHelper.navigateToUrl(PageRoute.JOB_BOARD);
        }
      } else if (
        location.pathname.includes(urljoin(PageRoute.JOB_BOARD, JobBoardRoute.JOB_LIST)) ||
        location.pathname.includes(
          urljoin(PageRoute.JOB_BOARD, JobBoardRoute.JOB_DETAIL.replace(':jobID', '')),
        )
      ) {
      }
    }
  }, [userProfile, prevCompanies]);

  useEffect(() => {
    if (location.pathname.includes('/pi')) {
      onHandleShowingPopup(props.userRole, userProfile, openPopup);
    }
  }, [resData, userProfile, prevPathname, openPopup]);

  const alertModalFreeUser = (subscriptionID: number) => {
    modalStack.dismissAll();
    if (props.userRole === UserRoleType.SUPPLIER) {
      modalStack.push(OptionModal, {
        data: {
          modalSize: ModalSize.MD,
          title: t('SubscriptionPlan.Modal.FreeTrialStartTitle'),
          image: FreeTrialImg,
          firstOptionText: t('SubscriptionPlan.Modal.BtnStartFreeTrial'),
          secondOptionText: t('SubscriptionPlan.Modal.BtnSelectPackage'),
        },
        onClose: () => {},
        hasCloseBtn: true,
        onFirstOptionClick: () => showTermModal(subscriptionID),
        onSecondOptionClick: () => CommonHelper.navigateToUrl(PageRoute.SUBSCRIPTION_PLAN),
        children: (
          <div className="mt-16">
            <p>{t('SubscriptionPlan.Modal.FreeTrialDes')}</p>
          </div>
        ),
      });
    } else {
      return <></>;
    }
  };

  const alertModalFreeTrialAlmostOver = (
    packagePlan: string,
    isActive: boolean,
    expireDate: number,
  ) => {
    modalStack.push(OptionModal, {
      data: {
        modalSize: ModalSize.MD,
        title: t('SubscriptionPlan.Modal.FreeTrailAlmostTitle'),
        image: PackagePlanExpire,
        firstOptionText: t('SubscriptionPlan.Modal.BtnSelectPackage'),
        secondOptionText: t('SubscriptionPlan.Modal.BtnBackHome'),
        isActive: isActive,
      },
      onClose: () => {},
      hasCloseBtn: true,
      onSecondOptionClick: () => CommonHelper.navigateToUrl(PageRoute.SUBSCRIPTION_PLAN),
      children: (
        <div className="mt-16">
          <p>
            {props.userRole === UserRoleType.BUYER
              ? t('SubscriptionPlan.Modal.FreeTrialDes')
              : t('SubscriptionPlan.Modal.FreeTrialAlmostOver', { diffDays: expireDate })}
          </p>
          {props.userRole === UserRoleType.SUPPLIER && (
            <p>
              {t('SubscriptionPlan.Modal.AnyQuestions')}
              <br />
              {t('SubscriptionPlan.Modal.CustomerCare', { phoneNumber: CUST_CARE_PHONE })}{' '}
              <a href="mailto:customercare@pantavanij.com" className="btn-link">
                customercare@pantavanij.com
              </a>
              <br />
              {t('SubscriptionPlan.Modal.SupplierManagement', { phoneNumber: '02-034-4488' })}{' '}
              <a href="mailto:SupplierWebWork@pantavanij.com" className="btn-link">
                SupplierWebWork@pantavanij.com
              </a>
            </p>
          )}
        </div>
      ),
    });
  };

  const alertModalPackageAlmostOver = (
    packagePlan: string,
    isActive: boolean,
    expireDate: number,
  ) => {
    modalStack.push(OptionModal, {
      data: {
        modalSize: ModalSize.MD,
        title: t('SubscriptionPlan.Modal.PackageAlmostTitle'),
        image: PackagePlanExpire,
        firstOptionText: t('SubscriptionPlan.Modal.BtnOK'),
        isActive: isActive,
        isSingleButton: true,
      },
      onClose: () => {},
      hasCloseBtn: true,
      children: (
        <div className="mt-16">
          <p>
            {t('SubscriptionPlan.Modal.YourPackageIsOver', {
              packagePlan,
              expireDate,
            })}{' '}
            {expireDate > 1 ? t('SubscriptionPlan.Modal.Days') : t('SubscriptionPlan.Modal.Day')}.
          </p>
          {props.userRole === UserRoleType.SUPPLIER && (
            <p>
              {t('SubscriptionPlan.Modal.AnyQuestions')}
              <br />
              {t('SubscriptionPlan.Modal.CustomerCare', { phoneNumber: CUST_CARE_PHONE })}{' '}
              <a href="mailto:customercare@pantavanij.com" className="btn-link">
                customercare@pantavanij.com
              </a>
              <br />
              {t('SubscriptionPlan.Modal.SupplierManagement', { phoneNumber: '02-034-4488' })}{' '}
              <a href="mailto:SupplierWebWork@pantavanij.com" className="btn-link">
                SupplierWebWork@pantavanij.com
              </a>
            </p>
          )}
        </div>
      ),
    });
  };

  const alertModalPackageExpired = (
    userRole: UserRoleType | undefined,
    packagePlan: string = '',
  ) => {
    modalStack.dismissAll();
    modalStack.push(OptionModal, {
      data: {
        modalSize: ModalSize.MD,
        title: t('SubscriptionPlan.Modal.PackageExpired'),
        image: userRole === UserRoleType.BUYER ? FreeTrialImg : PackagePlanExpire,
        firstOptionText: t('SubscriptionPlan.Modal.BtnSelectPackage'),
        isActive: userRole === UserRoleType.BUYER ? false : true,
        isSingleButton: true,
        isSingleButtonReturnFirstOptionClick: true,
      },
      onClose: () => {
        window.location.assign(PageRoute.LOGOUT);
      },
      hasCloseBtn: true,
      onFirstOptionClick: () => CommonHelper.navigateToUrl(PageRoute.SUBSCRIPTION_PLAN),

      children: (
        <div className="mt-16">
          <p>{t('SubscriptionPlan.Modal.PackageIsOver', { packagePlan: packagePlan })}</p>
          {props.userRole === UserRoleType.SUPPLIER && (
            <p>
              {t('SubscriptionPlan.Modal.AnyQuestions')}
              <br />
              {t('SubscriptionPlan.Modal.CustomerCare', { phoneNumber: CUST_CARE_PHONE })}{' '}
              <a href="mailto:customercare@pantavanij.com" className="btn-link">
                customercare@pantavanij.com
              </a>
              <br />
              {t('SubscriptionPlan.Modal.SupplierManagement', { phoneNumber: '02-034-4488' })}{' '}
              <a href="mailto:SupplierWebWork@pantavanij.com" className="btn-link">
                SupplierWebWork@pantavanij.com
              </a>
            </p>
          )}
        </div>
      ),
    });
  };

  const almostOver = (
    subscriptionPlan: string,
    packagePlan: string = '',
    isActive: boolean,
    expireDate: number,
  ) => {
    switch (subscriptionPlan) {
      case SubscriptionPlanEnum.FREE_TRIAL:
        alertModalFreeTrialAlmostOver(packagePlan, isActive, expireDate);
        break;
      default:
        alertModalPackageAlmostOver(packagePlan, isActive, expireDate);
        break;
    }
  };

  // TODO: check if it's usable
  const alertModalPackageCancelled = (title: string, reason?: string) => {
    modalStack.push(OptionModal, {
      data: {
        modalSize: ModalSize.MD,
        title: t('SubscriptionPlan.Modal.FreeTrialStartTitle'),
        image: FreeTrialImg,
        firstOptionText: t('SubscriptionPlan.Modal.BtnStartFreeTrial'),
        secondOptionText: t('SubscriptionPlan.Modal.BtnSelectPackage'),
      },
      onClose: () => {},
      hasCloseBtn: true,
      children: (
        <div className="mt-16">
          <p>{t('SubscriptionPlan.Modal.FreeTrialDes')}</p>
        </div>
      ),
    });
  };

  // TODO: check if it's usable
  const alertModalPackagePaymentReject = (title: string, reason?: string) => {
    modalStack.push(OptionModal, {
      data: {
        modalSize: ModalSize.MD,
        title: t('SubscriptionPlan.Modal.FreeTrialStartTitle'),
        image: FreeTrialImg,
        firstOptionText: t('SubscriptionPlan.Modal.BtnStartFreeTrial'),
        secondOptionText: t('SubscriptionPlan.Modal.BtnSelectPackage'),
      },
      onClose: () => {},
      hasCloseBtn: true,
      children: (
        <div className="mt-16">
          <p>{t('SubscriptionPlan.Modal.FreeTrialDes')}</p>
        </div>
      ),
    });
  };

  const alertModalSuspendSupplier = (packageName: string) => {
    if (props.userRole === UserRoleType.SUPPLIER) {
      const modalBody = resData?.reason
        ? t('ModalSuspendSupplier.BodyWithReason', {
            package: CommonHelper.getPackageName(packageName),
            reason: resData?.reason,
          })
        : t('ModalSuspendSupplier.Body', {
            package: CommonHelper.getPackageName(packageName),
          });
      modalStack.push(ConfirmModal, {
        data: {
          modalSize: ModalSize.MD,
          title: t('ModalSuspendSupplier.Title'),
          submitText: t('ModalSuspendSupplier.Close'),
          cancelText: t('ModalSuspendSupplier.Close'),
        },
        onClose: () => {},
        showCancel: false,
        onSubmit: () => {
          CommonHelper.navigateToUrl(APIConstant.supplierConnect.redirect);
        },
        children: (
          <>
            <div
              dangerouslySetInnerHTML={{
                __html: modalBody,
              }}
            ></div>
          </>
        ),
      });
    }
  };

  // TODO: check if it's usable
  const alertModalUserNoPerMission = async () => {
    let subscriptionData: SubscriptionData | undefined;
    try {
      subscriptionData = await AuthorizeEmarketplaceClient.getSubscriptionData(
        userProfile.companies[0].eMarketplaceCompanyID,
      ).toPromise();
    } catch (err) {}
    modalStack.push(AlertModal, {
      title: t('ModalNoPermission.Title'),
      closeText: t('ModalNoPermission.Close'),
      elementID: '',
      onClose: () => {
        CommonHelper.navigateToUrl(PageRoute.LOGOUT);
      },
      children: (
        <>
          <div
            dangerouslySetInnerHTML={{
              __html: t('ModalNoPermission.Body', {
                package: subscriptionData?.title,
                adminName:
                  subscriptionData?.adminFirstNameLocal || subscriptionData?.adminLastNameLocal
                    ? `${subscriptionData?.adminFirstNameLocal} ${subscriptionData?.adminLastNameLocal}`.trim()
                    : '',
                adminEmail: subscriptionData?.adminEmail,
              }),
            }}
          ></div>
        </>
      ),
    });
  };

  // TODO: check if it's usable
  const alertModalAlmostExpire = (plan: string, day: number) => {
    if (props.userRole === UserRoleType.SUPPLIER) {
      if (plan === 'FREE') {
        modalStack.push(ConfirmModal, {
          data: {
            modalSize: ModalSize.MD,
            title: t('ModalFreeAlmostOver.Title'),
            submitText: t('ModalFreeAlmostOver.SubmitText'),
            cancelText: t('ModalFreeAlmostOver.CloseText'),
          },
          onClose: () => {},
          onSubmit: () => {
            CommonHelper.navigateToUrl(PageRoute.SUBSCRIPTION_PLAN);
          },
          children: (
            <>
              <div
                dangerouslySetInnerHTML={{
                  __html: t('ModalFreeAlmostOver.Body', { day: day }),
                }}
              ></div>
            </>
          ),
        });
      } else {
        modalStack.push(AlertModal, {
          title: t('ModalAlmostOver.Title'),
          closeText: t('ModalAlmostOver.CloseText'),
          elementID: '',
          onClose: () => {},
          children: (
            <>
              <div
                dangerouslySetInnerHTML={{
                  __html: t('ModalAlmostOver.Body', { package: plan, day: day }),
                }}
              ></div>
            </>
          ),
        });
      }
    } else {
      return <></>;
    }
  };

  // TODO: check if it's usable
  const handleExpireDate = (plan: string, diffDays: number, supplierSubscriptionID: number) => {
    if (plan === 'FREE') {
      if (diffDays <= 10) {
        const cookieAlertFreeExpire = Cookies.get(
          CookieName.SUPPLIER_ALERT_FREE_ALMOST_EXPIRE_TIME + supplierSubscriptionID.toString(),
        );
        if (
          isUndefined(cookieAlertFreeExpire) ||
          new Date().setHours(0, 0, 0, 0) - parseInt(cookieAlertFreeExpire) > 0
        ) {
          Cookies.set(
            CookieName.SUPPLIER_ALERT_FREE_ALMOST_EXPIRE_TIME + supplierSubscriptionID.toString(),
            new Date().setHours(0, 0, 0, 0).toString(),
          );
          alertModalAlmostExpire(plan, diffDays);
        }
      }
    } else {
      if (diffDays <= 7) {
        Cookies.remove(
          CookieName.SUPPLIER_ALERT_ALMOST_EXPIRE_TIME_SECOND_PERIOD +
            supplierSubscriptionID.toString(),
        );
        const cookieAlertExpire = Cookies.get(
          CookieName.SUPPLIER_ALERT_ALMOST_EXPIRE_TIME_THIRD_PERIOD +
            supplierSubscriptionID.toString(),
        );
        if (isUndefined(cookieAlertExpire)) {
          Cookies.set(
            CookieName.SUPPLIER_ALERT_ALMOST_EXPIRE_TIME_THIRD_PERIOD +
              supplierSubscriptionID.toString(),
            new Date().setHours(0, 0, 0, 0).toString(),
          );
          alertModalAlmostExpire(plan, diffDays);
        }
      }
      if (diffDays >= 8 && diffDays <= 15) {
        Cookies.remove(
          CookieName.SUPPLIER_ALERT_ALMOST_EXPIRE_TIME_FIRST_PERIOD +
            supplierSubscriptionID.toString(),
        );
        const cookieAlertExpire = Cookies.get(
          CookieName.SUPPLIER_ALERT_ALMOST_EXPIRE_TIME_SECOND_PERIOD +
            supplierSubscriptionID.toString(),
        );
        if (isUndefined(cookieAlertExpire)) {
          Cookies.set(
            CookieName.SUPPLIER_ALERT_ALMOST_EXPIRE_TIME_SECOND_PERIOD +
              supplierSubscriptionID.toString(),
            new Date().setHours(0, 0, 0, 0).toString(),
          );
          alertModalAlmostExpire(plan, diffDays);
        }
      }
    }
  };

  return (
    <EMarketplaceLayoutProvider subscriptionInfo={resData}>
      <PIModuleProvider>
        <Header />
        {fixedBreadcrumb ? (
          <>
            <Breadcrumb fixed={true} />
            <div className="container-wrapper">
              <EmarketplaceAlertBar />
              {props.children}
            </div>
          </>
        ) : fixedBreadcrumbNoContainerWrapper ? (
          <>
            <Breadcrumb fixed={true} />
            <div style={{ minHeight: 'calc(100% - 100px)' }}>
              <EmarketplaceAlertBar />
              <Banner />
              {props.children}
            </div>
          </>
        ) : fixedWrapperHasLeftSideBar ? (
          <div className="container-wrapper fixed-left-sidebar">
            <EmarketplaceAlertBar />
            <Breadcrumb />
            <Banner />
            {props.children}
          </div>
        ) : (
          <div className="container-fluid no-padding-bottom">
            <EmarketplaceAlertBar />
            <Breadcrumb />
            <Banner />
            {props.children}
          </div>
        )}
        <Footer userRole={props.userRole} />
      </PIModuleProvider>
    </EMarketplaceLayoutProvider>
  );
}
